import React, { useState } from 'react';
import { useLanguage } from '@/contexts';
import { usePasswordDisplay } from '@/hooks';
import { validatePassword } from '@/utils/validatePassword';
import { Button } from '../ui';
import BaseInput from '../ui/BaseInput';
import MaskPasswordButton from '../ui/shared/MaskPasswordButton';
import { LoginFormData } from '../../types/auth';

interface LoginFormProps {
  onSubmit: (data: LoginFormData) => void;
  loading?: boolean;
  onSwitchToSignUp?: () => void;
  fieldErrors?: Record<string, string>;
  generalError?: string | null;
}

const LoginForm = (props: LoginFormProps) => {
  const { t } = useLanguage();
  const { showPassword, setShowPassword } = usePasswordDisplay();
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    props.onSubmit(formData);
  };

  const handleChange = (field: keyof LoginFormData) => (value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (field === "password") {
      const errs = validatePassword(value);
      setPasswordErrors(errs);
    }
  };

  const { fieldErrors = {}, generalError } = props;
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {generalError && (
        <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm text-center">
          {generalError}
        </div>
      )}
      <BaseInput
        label="Email Address"
        name="email"
        type="email"
        value={formData.email}
        onChange={handleChange('email')}
        error={fieldErrors.email}
        required
        autoComplete="email"
        placeholder={t("email")}
      />

      <BaseInput
        label="Password"
        name="password"
        type={showPassword ? 'text' : 'password'}
        value={formData.password}
        onChange={handleChange('password')}
        error={
          passwordErrors.length > 0
            ? passwordErrors.map((err) => t(err)).join(', ')
            : fieldErrors.password
        }
        required
        autoComplete="current-password"
        placeholder={t("password")}
        rightAddon={<MaskPasswordButton showPassword={showPassword} setShowPassword={setShowPassword} />}
      />

      <Button
        type="submit"
        loading={props.loading}
        className="w-full py-3 bg-brand-dark-blue hover:bg-brand-electric-blue text-white font-medium rounded-md mt-3"
      >
        {t('login')}
      </Button>

      <div className="text-center text-sm text-gray-600 mt-2">
        <p className="mb-2">
          <a 
            href="#" 
            className="text-brand-electric-blue hover:text-brand-dark-blue" 
            onClick={(e) => {
              e.preventDefault();
              props.onSwitchToSignUp?.();
            }}
          >
            {t('signUp')}
          </a>
        </p>
        <p>
          <a 
            href="#" 
            className="text-brand-electric-blue hover:text-brand-dark-blue" 
            onClick={(e) => {
              e.preventDefault();
              window.location.href = '/forgot-password';
            }}
          >
            {t('forgotPassword')}
          </a>
        </p>
      </div>
    </form>
  );
};

export { LoginForm };
